'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbProps {
  customItems?: Array<{ label: string; path?: string }>;
}

const Breadcrumb = ({ customItems }: BreadcrumbProps) => {
  const pathname = usePathname();

  const routeLabels: Record<string, string> = {
    'main-dashboard': 'Dashboard',
    'process-management': 'Process Management',
    'process-creation-wizard': 'Process Wizard',
    'dynamic-form-builder': 'Form Builder',
    'task-management-center': 'Task Center',
    'template-gallery': 'Template Gallery',
    'personal-template-manager': 'My Templates',
    'portal-builder': 'Portal Builder',
    'custom-portal-view': 'Portal View',
    'analytics-dashboard': 'Analytics',
    'user-administration': 'User Management',
    'delegation-control-center': 'Delegation Control',
  };

  const generateBreadcrumbs = () => {
    if (customItems) {
      return customItems;
    }

    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs = [{ label: 'Home', path: '/main-dashboard' }];

    paths.forEach((path, index) => {
      const label = routeLabels[path] || path.replace(/-/g, ' ');
      const fullPath = '/' + paths.slice(0, index + 1).join('/');
      breadcrumbs.push({
        label: label.charAt(0).toUpperCase() + label.slice(1),
        path: index === paths.length - 1 ? undefined : fullPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav data-component-id="src/components/common/Breadcrumb.tsx:56:4" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="56" data-component-file="Breadcrumb.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22mb-6%22%7D" aria-label="Breadcrumb" className="mb-6">
      <ol data-component-id="src/components/common/Breadcrumb.tsx:57:6" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="57" data-component-file="Breadcrumb.tsx" data-component-name="ol" data-component-content="%7B%22elementName%22%3A%22ol%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20text-sm%22%7D" className="flex items-center gap-2 text-sm">
        {breadcrumbs.map((crumb, index) => (
          <li data-component-id="src/components/common/Breadcrumb.tsx:59:10" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="59" data-component-file="Breadcrumb.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" key={index} className="flex items-center gap-2">
            {index > 0 && (
              <Icon data-component-id="src/components/common/Breadcrumb.tsx:61:14" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="61" data-component-file="Breadcrumb.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
                name="ChevronRightIcon"
                size={16}
                className="text-muted-foreground"
              />
            )}
            {crumb.path ? (
              <Link data-component-id="src/components/common/Breadcrumb.tsx:68:14" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="68" data-component-file="Breadcrumb.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-accent%20hover%3Atext-accent%2F80%20transition-micro%22%7D"
                href={crumb.path}
                className="text-accent hover:text-accent/80 transition-micro"
              >
                {crumb.label}
              </Link>
            ) : (
              <span data-component-id="src/components/common/Breadcrumb.tsx:75:14" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="75" data-component-file="Breadcrumb.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-foreground%20font-medium%22%7D" className="text-foreground font-medium">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;